import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('identium_token');
    const stored = localStorage.getItem('identium_user');
    if (token && stored) {
      try {
        setUser(JSON.parse(stored));
      } catch {
        localStorage.removeItem('identium_token');
        localStorage.removeItem('identium_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password, company) => {
    const res = await api.post('/auth/login', { username, password, company });
    const { token, user: userData } = res.data;
    localStorage.setItem('identium_token', token);
    localStorage.setItem('identium_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  // Replace the local user record (e.g. after password change returns a new token + user)
  const setSession = (token, userData) => {
    localStorage.setItem('identium_token', token);
    localStorage.setItem('identium_user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {}
    // Capture which company the user was signed in to BEFORE we wipe
    // localStorage. Without this, logout always landed on the generic
    // /login URL — which then re-shows the Company picker, even though
    // we know exactly which company the user just left.
    const lastCompany =
      localStorage.getItem('identium_last_company') ||
      // Belt-and-braces: derive from the cached user blob if the
      // last-company key was somehow cleared. The login flow stuffs
      // tenant.slug into the user object before saveSession.
      (() => {
        try { return JSON.parse(localStorage.getItem('identium_user') || '{}')?.tenant?.slug || null; }
        catch { return null; }
      })();
    localStorage.removeItem('identium_token');
    localStorage.removeItem('identium_user');
    // Keep identium_last_company so the next login pre-fills the Company
    // field (already-saved behaviour — don't clear it).
    setUser(null);
    // Hard navigate so the Login page mounts fresh (its useEffect chain
    // reads the URL slug). React Router's navigate would also work but
    // a few callers wrap logout in different routing contexts.
    const BASE = process.env.PUBLIC_URL || '';
    if (lastCompany && /^[a-z0-9-]+$/.test(lastCompany)) {
      window.location.href = `${BASE}/${lastCompany}/login`;
    } else {
      window.location.href = `${BASE}/login`;
    }
  }, []);

  const hasPermission = useCallback((module) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.permissions && user.permissions.includes(module);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setSession, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
